import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { FlaskConical, CheckCircle2, XCircle, Play, History, AlertTriangle, Radar } from 'lucide-react'
import toast from 'react-hot-toast'
import { getSession } from '../lib/session'

// The test-automation layer inside Governance: SIT pass-rate, the last run, any
// current regressions (open failures), and recent run history — all from the QA
// service's real test_runs (recorded by the nightly qa-run cron + manual runs).
const JOB_LABEL = {
  'mi-sync': 'Metrics sync', 'growth-scan': 'Growth scan', 'connector-health': 'Connector health',
  'notif-digest': 'Daily digest', 'qa-run': 'QA suite',
}

export default function GovernanceQuality() {
  const ns = getSession()?.namespaceId
  const [q, setQ] = useState(null)
  const [running, setRunning] = useState(false)
  const [auto, setAuto] = useState(null)
  const [showCfg, setShowCfg] = useState(false)

  const load = () => {
    if (!ns) return
    axios.get('/api/v1/governance/quality').then((r) => setQ(r.data)).catch(() => setQ(null))
    axios.get('/api/v1/mi/site/automation').then((r) => setAuto(r.data)).catch(() => setAuto(null))
  }
  useEffect(load, [ns])

  const patchAuto = async (patch) => {
    setAuto((a) => ({ ...a, ...patch, jobs: { ...(a?.jobs || {}), ...(patch.jobs || {}) } }))
    try {
      await axios.put('/api/v1/mi/site/automation', patch)
      toast.success('Automation settings saved')
    } catch (e) {
      toast.error('Could not save settings')
      load()
    }
  }

  const runNow = async () => {
    setRunning(true)
    try {
      await axios.post('/api/v1/agent/qa/run', {})
      toast.success('SIT suite run — refreshing results')
      load()
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not start the run')
    } finally {
      setRunning(false)
    }
  }

  if (!q) return null
  const rate = q.passRate
  const tone = rate == null ? 'text-gray-500' : rate >= 100 ? 'text-green-700' : rate >= 80 ? 'text-amber-700' : 'text-red-700'
  const t = q.totals || {}
  const failures = q.openFailures || []

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <FlaskConical size={16} className="text-primary-600" /> Quality &amp; Testing
          </h3>
          <p className="text-sm text-gray-500">System-integration suite — run nightly by the QA agent and on demand. Every service validated end to end.</p>
        </div>
        <button onClick={runNow} disabled={running}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 text-white text-sm font-semibold px-3 py-1.5 hover:bg-primary-700 disabled:opacity-60">
          <Play size={14} /> {running ? 'Running…' : 'Run SIT now'}
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {/* Pass rate */}
        <div className="card">
          <div className="flex items-center gap-2 text-gray-500 mb-3"><CheckCircle2 size={16} /><span className="text-xs font-bold uppercase tracking-wide">Pass rate</span></div>
          <div className="flex items-baseline gap-1">
            {rate == null
              ? <span className="text-2xl font-bold text-gray-400">No runs yet</span>
              : <><span className={`text-3xl font-bold tabular-nums ${tone}`}>{rate}</span><span className="text-gray-400 text-lg">%</span></>}
          </div>
          {q.lastRun && (
            <p className="mt-1.5 text-sm text-gray-600">
              {q.lastRun.passed}/{q.lastRun.total} passed · {new Date(q.lastRun.run_at).toLocaleString()}
            </p>
          )}
          {t.runs != null && <p className="mt-2 text-[11px] text-gray-400 uppercase tracking-wide">{t.runs} run{t.runs === 1 ? '' : 's'} recorded</p>}
        </div>

        {/* Open failures (current regressions) */}
        <div className="card">
          <div className="flex items-center gap-2 text-gray-500 mb-3"><XCircle size={16} /><span className="text-xs font-bold uppercase tracking-wide">Open failures</span></div>
          {failures.length === 0
            ? <p className="text-sm text-green-700 flex items-center gap-1.5"><CheckCircle2 size={15} /> No failing checks — all green.</p>
            : (
              <div className="space-y-1.5 max-h-44 overflow-y-auto">
                {failures.map((f, i) => (
                  <div key={i} className="text-xs">
                    <span className="font-semibold text-red-700">{f.service}</span>
                    <span className="text-gray-500"> · {f.name}</span>
                    {f.detail && <div className="text-gray-400">{f.detail}</div>}
                  </div>
                ))}
              </div>
            )}
        </div>

        {/* Run history */}
        <div className="card">
          <div className="flex items-center gap-2 text-gray-500 mb-3"><History size={16} /><span className="text-xs font-bold uppercase tracking-wide">Run history</span></div>
          {(!q.history || q.history.length === 0)
            ? <p className="text-sm text-gray-400">No runs recorded yet.</p>
            : (
              <div className="space-y-1.5 max-h-44 overflow-y-auto">
                {q.history.map((r) => (
                  <div key={r.id} className="text-xs flex items-center justify-between gap-2">
                    <span className={r.failed ? 'text-red-700' : 'text-green-700'}>
                      {r.passed}/{r.total}
                    </span>
                    <span className="text-gray-400">{new Date(r.run_at).toLocaleDateString()} {new Date(r.run_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    <span className="text-gray-400 font-mono">{r.triggered_by || '—'}</span>
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>

      {/* Cron observability + controls — the platform-wide proactive sweep */}
      {(q.lastSweep || auto) && (
        <div className="card mt-4">
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
            <div className="flex items-center gap-2 text-gray-500"><Radar size={16} /><span className="text-xs font-bold uppercase tracking-wide">Proactive automation</span></div>
            {q.lastSweep && (
              <div className="text-sm text-gray-700">
                Last sweep <span className="font-semibold">{new Date(q.lastSweep.ranAt).toLocaleString()}</span>
                {' · '}{q.lastSweep.tenants} tenant{q.lastSweep.tenants === 1 ? '' : 's'}
                {' · '}<span className="text-green-700 font-semibold">{q.lastSweep.enqueued} jobs</span> fanned out
                {q.lastSweep.failed > 0 && <span className="text-red-700 font-semibold"> · {q.lastSweep.failed} failed</span>}
              </div>
            )}
            {auto && (
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <span className={auto.enabled ? 'text-green-700 font-semibold' : 'text-gray-400'}>{auto.enabled ? 'On' : 'Off'}</span>
                  <input type="checkbox" checked={!!auto.enabled} onChange={(e) => patchAuto({ enabled: e.target.checked })} />
                </label>
                <button onClick={() => setShowCfg((s) => !s)} className="text-xs text-primary-600 underline">{showCfg ? 'Hide cadence' : 'Cadence'}</button>
              </div>
            )}
          </div>

          {showCfg && auto && (
            <div className="mt-3 pt-3 border-t border-gray-100 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
              {Object.keys(auto.jobs || {}).map((job) => (
                <label key={job} className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-gray-700">{JOB_LABEL[job] || job}</span>
                  <select
                    value={auto.jobs[job]}
                    disabled={!auto.enabled}
                    onChange={(e) => patchAuto({ jobs: { [job]: e.target.value } })}
                    className="rounded-md border border-gray-200 text-xs px-2 py-1 bg-white disabled:opacity-50">
                    {(auto.cadences || ['daily', 'weekly', 'hourly', 'off']).map((c) => (
                      <option key={c} value={c}>{c}{c === 'hourly' && job !== 'connector-health' ? ' (n/a)' : ''}</option>
                    ))}
                  </select>
                </label>
              ))}
              <p className="text-[11px] text-gray-400 lg:col-span-3">Weekly jobs run Mondays (UTC). Hourly is honored for connector health; other jobs treat it as daily.</p>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
